import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

import { API } from "../../config/api";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login or register
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = mode === "login" ? "/admin/login" : "/admin/register";
      const body = mode === "login" 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("admin_token", data.access_token);
        toast.success(mode === "login" ? "Welcome back!" : "Account created!");
        navigate("/admin");
      } else {
        toast.error(data.detail || "Authentication failed");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-kerala-cream flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_244098db-6247-4607-8c98-aa9a63ac3c07/artifacts/z92rohdv_WhatsApp%20Image%202026-02-09%20at%208.18.57%20PM.jpeg"
              alt="Tatvamasi Logo"
              className="w-16 h-16 object-contain"
            />
            <div className="text-left">
              <h1 className="font-heading font-semibold text-xl text-kerala-green">Tatvamasi</h1>
              <p className="text-sm text-gray-500">Kerala Hindu's of London Ontario</p>
            </div>
          </Link>
        </div>

        {/* Form Card */}
        <div className="bg-white border border-kerala-gold/20 rounded-sm p-8 shadow-lg">
          <h2 className="font-heading text-2xl text-kerala-green text-center mb-6">
            {mode === "login" ? "Admin Login" : "Create Admin Account"}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1"
                  required
                  data-testid="admin-name-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1"
                required
                data-testid="admin-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-1"
                required
                minLength={6}
                data-testid="admin-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-kerala-terracotta hover:bg-[#A03500] text-white rounded-full px-8 py-4 text-lg font-medium transition-colors duration-200"
              disabled={loading}
              data-testid="admin-submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  <span>{mode === "login" ? "Signing in..." : "Creating account..."}</span>
                </span>
              ) : (
                <span>{mode === "login" ? "Sign In" : "Create Account"}</span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-kerala-green hover:text-kerala-terracotta text-sm"
              data-testid="toggle-mode-btn"
            >
              {mode === "login" 
                ? "Need an account? Register" 
                : "Already have an account? Sign in"}
            </button>
          </div>
        </div>

        <p className="text-center mt-6 text-sm text-gray-500">
          <Link to="/" className="text-kerala-green hover:text-kerala-terracotta">
            ← Back to Website
          </Link>
        </p>
      </div>
    </div>
  );
}
